import React, { Suspense } from 'react';
import { REPORT_SENTIMENT_ENABLED } from '../../../lib/report-entitlements';
import { Skeleton } from '../../ui/Skeleton';
import { ThemeCards } from './ThemeCards';
import { QuoteSpotlight } from './QuoteSpotlight';
import { InsightsGrid } from './InsightsGrid';
import { ActionsPanel } from './ActionsPanel';

// Lazy-load recharts-heavy components
const AggregatedSentimentBar = React.lazy(() => import('./AggregatedSentimentBar'));
const AggregatedSentimentOverview = React.lazy(() => import('./AggregatedSentimentOverview'));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AggregatedReportData {
  /** False when the server returns only ordinary report fields. */
  advancedAnalyticsAvailable?: boolean;
  tldr: string;
  keyFindings: Array<{ insight: string; category: 'insight' | 'pain_point' | 'opportunity' | 'data_point' }>;
  notableQuotes: Array<{
    text: string;
    context: string;
    turnIndex: number;
    timestamp: number;
    sentiment?: 'positive' | 'negative' | 'neutral';
    respondentName: string;
    responseNumber: number;
  }>;
  recommendedActions: string[];
  openQuestions: string[];
  respondentSentiment: Array<{
    respondentLabel: string;
    avgScore: number;
    sentimentLabel: 'positive' | 'negative' | 'neutral';
    turnCount: number;
  }>;
  sentimentAnalysis: Array<{
    turnIndex: number;
    score: number;
    label: 'positive' | 'negative' | 'neutral';
    snippet: string;
    timestamp: number;
  }>;
  thematicAnalysis: Array<{
    /** Stable authored identity, absent for inferred themes. */
    sectionId?: string;
    theme: string;
    respondentPct: number;
    respondentCount: number;
    totalRespondents: number;
    avgSentiment: number;
    sentimentLabel: 'positive' | 'negative' | 'neutral';
  }>;
  meta: {
    respondentCount: number;
    generatedAt: string;
    includedReportCount: number;
    /** Pipeline version that built this report; see {@link CAMPAIGN_REPORT_VERSION}. */
    reportVersion?: number;
  };
}

/**
 * Campaign report pipeline version, mirrored from
 * functions/api/gemini/content-pipeline/campaign-aggregator.ts. A stored report
 * below it is rebuilt once when an owner opens it.
 */
export const CAMPAIGN_REPORT_VERSION = 2;

export interface AggregatedReportProps {
  data: AggregatedReportData;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Suspense fallback for lazy-loaded chart sections. Renders the SAME panel
 * chrome the live chart sits in (rounded card, padding, border) so the
 * report does not jump when the chart bundle resolves.
 */
function ChartSection({ heightClass = 'h-[340px]' }: { heightClass?: string }) {
  return (
    <div
      data-testid="report-chart-skeleton"
      aria-busy="true"
      className={`tone-volt rounded-[20px] p-6 ${heightClass}`}
    >
      <Skeleton height="100%" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Written campaign sections show at most this many insights, next steps and open questions. */
const REPORT_LIMIT = 3;

/** At most this many quotes and themes, the same as the Overview's highlights. */
const GALLERY_LIMIT = 6;

/**
 * The written sections of the one campaign report, in the Overview's card
 * design: key insights, key quotes, themes, sentiment and next steps. The
 * summary lives in its own card beside the Ask hollie suggestions.
 */
export const AggregatedReport: React.FC<AggregatedReportProps> = ({ data }) => {
  let inferredCount = 0;
  const themes = data.thematicAnalysis.filter(theme => {
    if (theme.sectionId) return true;
    inferredCount += 1;
    return inferredCount <= GALLERY_LIMIT;
  }).map((t) => ({
    sectionId: t.sectionId,
    theme: t.theme,
    percentage: t.respondentPct,
    avgSentiment: t.avgSentiment,
    sentimentLabel: t.sentimentLabel,
    turnIndices: [] as number[],
  }));

  /** Respondent counts instead of a percentage. */
  const labelFormatter = (theme: { sectionId?: string; theme: string; percentage: number }) => {
    const match = data.thematicAnalysis.find((t) => theme.sectionId
      ? t.sectionId === theme.sectionId
      : !t.sectionId && t.theme === theme.theme);
    return match ? `${match.respondentCount} of ${match.totalRespondents}` : `${Math.round(theme.percentage)}%`;
  };

  const advanced = data.advancedAnalyticsAvailable !== false;
  return (
    <div className="flex flex-col gap-5">
      <InsightsGrid findings={data.keyFindings} limit={REPORT_LIMIT} />
      <QuoteSpotlight quotes={data.notableQuotes.slice(0, GALLERY_LIMIT)} />
      {advanced && <ThemeCards themes={themes} labelFormatter={labelFormatter} />}
      {REPORT_SENTIMENT_ENABLED && advanced && (
        <>
          {/* No skeleton for the bar chart: it paints in smoothly on its own. */}
          <Suspense fallback={null}>
            <AggregatedSentimentBar data={data.respondentSentiment} />
          </Suspense>
          <Suspense fallback={<ChartSection heightClass="h-[320px]" />}>
            <AggregatedSentimentOverview sentimentData={data.sentimentAnalysis} respondentSentiment={data.respondentSentiment} />
          </Suspense>
        </>
      )}
      <ActionsPanel recommendedActions={data.recommendedActions} openQuestions={data.openQuestions} limit={REPORT_LIMIT} />
    </div>
  );
};

export default AggregatedReport;
